import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { dsWikiService } from '../services';
import type { Workspace as ApiWorkspace, Department as ApiDepartment, Location as ApiLocation, ArticleCategory as ApiTag, ArticleType as ApiArticleType } from '../services/types';

interface SubWorkspace {
  id: string;
  name: string;
  departmentId?: string;
  articleCount: number;
  isDefault?: boolean;
}

interface Workspace {
  id: string;
  name: string;
  description: string;
  icon: string;
  color: string;
  subWorkspaces: SubWorkspace[];
  isDefault?: boolean;
}

interface BreadcrumbItem {
  label: string;
  href?: string;
  icon?: string;
}

interface Department {
  id: string;
  name: string;
  workspaceId: string;
}

interface Location {
  id: string;
  name: string;
}

interface WikiHeaderProps {
  breadcrumbs?: BreadcrumbItem[];
  showFilterButton?: boolean;
  onFilterChange?: (filters: { workspace: string; department: string; location: string }) => void;
  openFilterPanelFor?: 'workspace' | 'department' | 'location' | null;
  onFilterPanelOpenChange?: (isOpen: boolean) => void;
}

export default function WikiHeader({
  breadcrumbs,
  showFilterButton = true,
  onFilterChange,
  openFilterPanelFor,
  onFilterPanelOpenChange
}: WikiHeaderProps) {
  const [showWorkspaces, setShowWorkspaces] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState<string>('maud-cdma');
  const [selectedSubWorkspace, setSelectedSubWorkspace] = useState<string | null>(null);

  // Refs for dropdown elements
  const workspaceSelectRef = useRef<HTMLSelectElement>(null);
  const departmentSelectRef = useRef<HTMLSelectElement>(null);
  const locationSelectRef = useRef<HTMLSelectElement>(null);

  // Handle external request to open filter panel
  useEffect(() => {
    if (openFilterPanelFor) {
      setShowFilters(true);
      setShowWorkspaces(false);
      // Focus the appropriate dropdown
      const timer = setTimeout(() => {
        if (openFilterPanelFor === 'workspace' && workspaceSelectRef.current) {
          workspaceSelectRef.current.focus();
        } else if (openFilterPanelFor === 'department' && departmentSelectRef.current) {
          departmentSelectRef.current.focus();
        } else if (openFilterPanelFor === 'location' && locationSelectRef.current) {
          locationSelectRef.current.focus();
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [openFilterPanelFor]);

  // Notify parent when filter panel state changes
  useEffect(() => {
    onFilterPanelOpenChange?.(showFilters);
  }, [showFilters, onFilterPanelOpenChange]);
  const [expandedWorkspaces, setExpandedWorkspaces] = useState<Set<string>>(new Set(['maud-cdma']));
  const [showCreateWorkspace, setShowCreateWorkspace] = useState(false);
  const [workspaceSearchQuery, setWorkspaceSearchQuery] = useState('');

  // Filter states
  const [filterWorkspace, setFilterWorkspace] = useState<string>('all');
  const [filterDepartment, setFilterDepartment] = useState<string>('all');
  const [filterLocation, setFilterLocation] = useState<string>('all');

  const [newWorkspace, setNewWorkspace] = useState({
    name: '',
    description: '',
    icon: 'ri-folder-line',
    color: 'blue'
  });

  // New dropdown and Circular/GO modal states
  const navigate = useNavigate();
  const [showNewDropdown, setShowNewDropdown] = useState(false);
  const [showCircularGOModal, setShowCircularGOModal] = useState(false);
  const [circularGOType, setCircularGOType] = useState<'circular' | 'go'>('circular');
  const [circularGOForm, setCircularGOForm] = useState({
    number: '',
    description: '',
    attachment: null as File | null,
    departments: [] as string[],
    locations: [] as string[],
    tags: [] as string[]
  });
  const [locationSearch, setLocationSearch] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const newDropdownRef = useRef<HTMLDivElement>(null);

  // API data states
  const [apiWorkspaces, setApiWorkspaces] = useState<ApiWorkspace[]>([]);
  const [apiDepartments, setApiDepartments] = useState<ApiDepartment[]>([]);
  const [apiLocations, setApiLocations] = useState<ApiLocation[]>([]);
  const [apiTags, setApiTags] = useState<ApiTag[]>([]);
  const [apiArticleTypes, setApiArticleTypes] = useState<ApiArticleType[]>([]);
  const [apiLoading, setApiLoading] = useState(true);

  // For dropdown positioning
  const newButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

  // Fetch master data from API on mount
  useEffect(() => {
    const fetchMasterData = async () => {
      setApiLoading(true);
      try {
        const [workspacesRes, departmentsRes, locationsRes, tagsRes, articleTypesRes] = await Promise.all([
          dsWikiService.getActiveWorkspaces(),
          dsWikiService.getActiveDepartments(),
          dsWikiService.getActiveLocations(),
          dsWikiService.getActiveArticleCategories(),
          dsWikiService.getActiveArticleTypes()
        ]);

        setApiWorkspaces(workspacesRes.data || []);
        setApiDepartments(departmentsRes.data || []);
        setApiLocations(locationsRes.data || []);
        setApiTags(tagsRes.data || []);
        setApiArticleTypes(articleTypesRes.data || []);

        // Set default selected workspace if available
        if (workspacesRes.data && workspacesRes.data.length > 0) {
          const defaultWs = workspacesRes.data.find(w => w.is_dflt_in) || workspacesRes.data[0];
          setSelectedWorkspace(String(defaultWs.dswiki_wrksp_id));
          setExpandedWorkspaces(new Set([String(defaultWs.dswiki_wrksp_id)]));
        }
      } catch (err: any) {
        console.error('Error fetching master data:', err);
      } finally {
        setApiLoading(false);
      }
    };

    fetchMasterData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (newDropdownRef.current && !newDropdownRef.current.contains(event.target as Node)) {
        setShowNewDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Transform API workspaces to UI format
  const workspaces: Workspace[] = apiWorkspaces.map(ws => ({
    id: String(ws.dswiki_wrksp_id),
    name: ws.wrksp_nm,
    description: ws.dscn_tx || '',
    icon: ws.icon_tx || 'ri-folder-line',
    color: ws.clr_cd ? ws.clr_cd.replace('#', '') : 'blue',
    isDefault: ws.is_dflt_in,
    subWorkspaces: apiDepartments
      .filter(d => d.a_in)
      .map(d => ({
        id: `${ws.dswiki_wrksp_id}-${d.dswiki_dpt_id}`,
        name: d.dpt_nm,
        departmentId: String(d.dswiki_dpt_id),
        articleCount: 0, // Will be populated when articles API is available
        isDefault: false
      }))
  }));

  const currentWorkspace = workspaces.find(w => w.id === selectedWorkspace);

  // Transform API departments to UI format
  const departments: Department[] = apiDepartments.map(d => ({
    id: String(d.dswiki_dpt_id),
    name: d.dpt_nm,
    workspaceId: workspaces.length > 0 ? workspaces[0].id : ''
  }));

  // Transform API locations to UI format
  const locations: Location[] = apiLocations.map(l => ({
    id: String(l.dswiki_loc_id),
    name: l.loc_nm
  }));

  // Filter departments based on selected workspace filter
  const filteredDepartments = filterWorkspace === 'all'
    ? departments
    : departments.filter(d => d.workspaceId === filterWorkspace);

  // Check if any filter is active
  const hasActiveFilters = filterWorkspace !== 'all' || filterDepartment !== 'all' || filterLocation !== 'all';

  // Handle filter changes
  const handleFilterChange = (type: 'workspace' | 'department' | 'location', value: string) => {
    if (type === 'workspace') {
      setFilterWorkspace(value);
      setFilterDepartment('all'); // Reset department when workspace changes
    } else if (type === 'department') {
      setFilterDepartment(value);
    } else {
      setFilterLocation(value);
    }

    if (onFilterChange) {
      onFilterChange({
        workspace: type === 'workspace' ? value : filterWorkspace,
        department: type === 'department' ? value : (type === 'workspace' ? 'all' : filterDepartment),
        location: type === 'location' ? value : filterLocation
      });
    }
  };

  const clearAllFilters = () => {
    setFilterWorkspace('all');
    setFilterDepartment('all');
    setFilterLocation('all');
    if (onFilterChange) {
      onFilterChange({ workspace: 'all', department: 'all', location: 'all' });
    }
  };

  const filteredWorkspaces = workspaces.filter(workspace => {
    if (!workspaceSearchQuery.trim()) return true;
    const query = workspaceSearchQuery.toLowerCase();
    const matchesName = workspace.name.toLowerCase().includes(query);
    const matchesDescription = workspace.description.toLowerCase().includes(query);
    const matchesSubWorkspace = workspace.subWorkspaces.some(sw =>
      sw.name.toLowerCase().includes(query)
    );
    return matchesName || matchesDescription || matchesSubWorkspace;
  });

  const toggleWorkspaceExpand = (workspaceId: string) => {
    setExpandedWorkspaces(prev => {
      const next = new Set(prev);
      if (next.has(workspaceId)) {
        next.delete(workspaceId);
      } else {
        next.add(workspaceId);
      }
      return next;
    });
  };

  const selectWorkspace = (workspaceId: string, subWorkspaceId?: string) => {
    setSelectedWorkspace(workspaceId);
    setSelectedSubWorkspace(subWorkspaceId || null);
    setShowWorkspaces(false);
  };

  const handleCreateWorkspace = async () => {
    if (!newWorkspace.name.trim()) return;

    try {
      // Call API to create workspace
      await dsWikiService.saveWorkspace({
        wrksp_nm: newWorkspace.name,
        dscn_tx: newWorkspace.description,
        icon_tx: newWorkspace.icon,
        clr_cd: newWorkspace.color,
        a_in: true
      });

      // Refresh workspaces list
      const workspacesRes = await dsWikiService.getActiveWorkspaces();
      setApiWorkspaces(workspacesRes.data || []);

      setNewWorkspace({
        name: '',
        description: '',
        icon: 'ri-folder-line',
        color: 'blue'
      });
      setShowCreateWorkspace(false);
    } catch (err: any) {
      console.error('Error creating workspace:', err);
      alert('Failed to create workspace: ' + (err.message || 'Unknown error'));
    }
  };

  const workspaceIcons = [
    { icon: 'ri-folder-line', label: 'Folder' },
    { icon: 'ri-building-4-line', label: 'Building' },
    { icon: 'ri-government-line', label: 'Government' },
    { icon: 'ri-map-pin-line', label: 'Map' },
    { icon: 'ri-team-line', label: 'Team' },
    { icon: 'ri-briefcase-line', label: 'Briefcase' },
    { icon: 'ri-code-line', label: 'Code' },
    { icon: 'ri-database-2-line', label: 'Database' }
  ];

  const workspaceColors = [
    { color: 'blue', bg: 'bg-blue-500', label: 'Blue' },
    { color: 'purple', bg: 'bg-purple-500', label: 'Purple' },
    { color: 'green', bg: 'bg-green-500', label: 'Green' },
    { color: 'orange', bg: 'bg-orange-500', label: 'Orange' },
    { color: 'red', bg: 'bg-red-500', label: 'Red' },
    { color: 'teal', bg: 'bg-teal-500', label: 'Teal' }
  ];

  const handleFiltersClick = () => {
    setShowFilters(!showFilters);
    if (showWorkspaces) setShowWorkspaces(false);
  };

  const handleWorkspaceClick = () => {
    setShowWorkspaces(!showWorkspaces);
    if (showFilters) setShowFilters(false);
  };

  return (
    <div className="sticky top-0 z-40 module-header shadow-lg">
      <div className="px-6 py-4">
        {/* Breadcrumbs */}
        {breadcrumbs && breadcrumbs.length > 0 && (
          <div className="flex items-center text-sm mb-3">
            <Link to="/app/ds-wiki" className="text-white/80 hover:text-white flex items-center">
              <i className="ri-home-4-line mr-1"></i>
              Dashboard
            </Link>
            {breadcrumbs.map((crumb, index) => (
              <span key={index} className="flex items-center">
                <i className="ri-arrow-right-s-line mx-2 text-white/60"></i>
                {crumb.href ? (
                  <Link to={crumb.href} className="text-white/80 hover:text-white flex items-center">
                    {crumb.icon && <i className={`${crumb.icon} mr-1`}></i>}
                    {crumb.label}
                  </Link>
                ) : (
                  <span className="bg-white/20 text-white px-3 py-1 rounded-full font-medium">
                    {crumb.label}
                  </span>
                )}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Knowledge Share - Wiki</h1>
            <p className="text-blue-100 text-sm">Your collaborative knowledge base for expertise sharing</p>
          </div>

          <div className="flex items-center space-x-3">
            {/* Workspace Button */}
            <button
              onClick={handleWorkspaceClick}
              className={`relative w-10 h-10 rounded-full border-2 transition-all duration-300 flex items-center justify-center font-bold text-lg ${
                showWorkspaces
                  ? 'bg-white border-white text-blue-600'
                  : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
              }`}
              title="Switch Workspace"
            >
              W
            </button>

            {/* Filter Button */}
            {showFilterButton && (
              <button
                onClick={handleFiltersClick}
                className={`relative w-10 h-10 rounded-full border-2 transition-all duration-300 flex items-center justify-center ${
                  showFilters
                    ? 'bg-white border-white text-blue-600'
                    : hasActiveFilters
                      ? 'bg-white/20 border-white/50 text-white'
                      : 'bg-white/10 border-white/30 text-white hover:bg-white/20'
                }`}
                title="Filter Articles"
              >
                <i className={`ri-filter-3-line text-lg transition-transform duration-300 ${showFilters ? 'rotate-180' : ''}`}></i>
                {hasActiveFilters && !showFilters && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full border-2 border-white"></span>
                )}
              </button>
            )}

            {/* New Dropdown */}
            <div className="relative" ref={newDropdownRef}>
              <button
                ref={newButtonRef}
                onClick={() => {
                  if (!showNewDropdown && newButtonRef.current) {
                    const rect = newButtonRef.current.getBoundingClientRect();
                    setDropdownPosition({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right
                    });
                  }
                  setShowNewDropdown(!showNewDropdown);
                }}
                className="bg-white text-blue-600 px-4 py-2 rounded-lg hover:bg-blue-50 flex items-center font-medium shadow-sm"
              >
                <i className="ri-add-line mr-2"></i>
                New
                <i className={`ri-arrow-down-s-line ml-2 transition-transform ${showNewDropdown ? 'rotate-180' : ''}`}></i>
              </button>

              {/* Dropdown Menu - Using fixed position to escape header stacking context */}
              {showNewDropdown && (
                <div
                  className="fixed w-72 bg-white rounded-xl shadow-2xl border border-gray-200 py-2 max-h-96 overflow-y-auto"
                  style={{ top: dropdownPosition.top, right: dropdownPosition.right, zIndex: 9999 }}
                >
                  <div className="px-3 py-2 border-b border-gray-100">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Select Article Type</p>
                  </div>
                  {apiArticleTypes.length > 0 ? (
                    apiArticleTypes.map((articleType) => (
                      <button
                        key={articleType.dswiki_artcl_type_id}
                        onClick={() => {
                          setShowNewDropdown(false);
                          navigate(`/app/ds-wiki/articles/create?type=${articleType.dswiki_artcl_type_id}`);
                        }}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 flex items-start gap-3 transition-colors"
                      >
                        <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <i className="ri-file-text-line text-blue-600"></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900">{articleType.artcl_type_nm}</p>
                          {articleType.dscn_tx && (
                            <p className="text-xs text-gray-500 line-clamp-1">{articleType.dscn_tx}</p>
                          )}
                        </div>
                        <i className="ri-arrow-right-s-line text-gray-400 mt-1"></i>
                      </button>
                    ))
                  ) : (
                    <div className="px-4 py-6 text-center">
                      <i className="ri-file-list-3-line text-3xl text-gray-300 mb-2"></i>
                      <p className="text-sm text-gray-500">No article types available</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Collapsible Workspace Selector */}
        <div className={`transition-all duration-300 ease-in-out ${showWorkspaces ? 'max-h-[350px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0 overflow-hidden'}`}>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 h-[350px] flex flex-col">
            <div className="flex items-center justify-between mb-4 flex-shrink-0">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <span className="font-bold text-white">W</span>
                </div>
                <div>
                  <h3 className="text-white font-semibold">Workspaces</h3>
                  <p className="text-blue-100 text-xs">Switch between your workspaces</p>
                </div>
              </div>

              {/* Search Workspaces */}
              <div className="flex-1 max-w-md mx-6">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 transform -translate-y-1/2 text-white/40"></i>
                  <input
                    type="text"
                    value={workspaceSearchQuery}
                    onChange={(e) => setWorkspaceSearchQuery(e.target.value)}
                    placeholder="Search workspaces..."
                    className="w-full pl-9 pr-8 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/50 focus:bg-white/15"
                  />
                  {workspaceSearchQuery && (
                    <button
                      onClick={() => setWorkspaceSearchQuery('')}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-3">
                <span className="text-xs text-blue-200 bg-white/10 px-2 py-1 rounded-full">
                  {currentWorkspace?.name}
                </span>
                <button
                  onClick={() => setShowCreateWorkspace(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-white text-blue-600 rounded-lg hover:bg-blue-50 text-sm font-medium transition-all"
                >
                  <i className="ri-add-line"></i>
                  Create Workspace
                </button>
              </div>
            </div>

            {/* Scrollable Workspace Grid */}
            <div className="flex-1 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.3) transparent' }}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {filteredWorkspaces.map(workspace => {
                  const isSelected = selectedWorkspace === workspace.id;
                  const isExpanded = expandedWorkspaces.has(workspace.id);
                  const colorClasses = {
                    blue: { bg: 'bg-blue-500', border: 'border-blue-400', hover: 'hover:bg-blue-500/20' },
                    purple: { bg: 'bg-purple-500', border: 'border-purple-400', hover: 'hover:bg-purple-500/20' },
                    green: { bg: 'bg-green-500', border: 'border-green-400', hover: 'hover:bg-green-500/20' },
                    orange: { bg: 'bg-orange-500', border: 'border-orange-400', hover: 'hover:bg-orange-500/20' },
                    red: { bg: 'bg-red-500', border: 'border-red-400', hover: 'hover:bg-red-500/20' },
                    teal: { bg: 'bg-teal-500', border: 'border-teal-400', hover: 'hover:bg-teal-500/20' }
                  }[workspace.color] || { bg: 'bg-gray-500', border: 'border-gray-400', hover: 'hover:bg-gray-500/20' };

                  return (
                    <div
                      key={workspace.id}
                      className={`rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? `bg-white/20 ${colorClasses.border}`
                          : `bg-white/5 border-white/10 ${colorClasses.hover}`
                      }`}
                    >
                      <div
                        className="p-3 cursor-pointer"
                        onClick={() => toggleWorkspaceExpand(workspace.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg ${colorClasses.bg} flex items-center justify-center`}>
                              <i className={`${workspace.icon} text-white text-xl`}></i>
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-white text-sm">{workspace.name}</span>
                                {workspace.isDefault && (
                                  <span className="text-[10px] bg-yellow-400 text-yellow-900 px-1.5 py-0.5 rounded font-medium">HOME</span>
                                )}
                              </div>
                              <p className="text-blue-200 text-xs">{workspace.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {isSelected && (
                              <i className="ri-check-line text-green-400"></i>
                            )}
                            <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-white/60`}></i>
                          </div>
                        </div>
                      </div>

                      {isExpanded && (
                        <div className="border-t border-white/10 px-3 py-2 space-y-1">
                          <button
                            onClick={() => selectWorkspace(workspace.id)}
                            className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                              isSelected && !selectedSubWorkspace
                                ? 'bg-white/20 text-white'
                                : 'text-blue-100 hover:bg-white/10'
                            }`}
                          >
                            <span className="flex items-center gap-2">
                              <i className="ri-folder-line"></i>
                              All Articles
                            </span>
                            <span className="text-xs text-blue-200">
                              {workspace.subWorkspaces.reduce((acc, sw) => acc + sw.articleCount, 0)}
                            </span>
                          </button>
                          {workspace.subWorkspaces.map(subWs => (
                            <button
                              key={subWs.id}
                              onClick={() => selectWorkspace(workspace.id, subWs.id)}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                                isSelected && selectedSubWorkspace === subWs.id
                                  ? 'bg-white/20 text-white'
                                  : 'text-blue-100 hover:bg-white/10'
                              }`}
                            >
                              <span className="flex items-center gap-2">
                                <i className={subWs.isDefault ? 'ri-home-4-line' : 'ri-folder-2-line'}></i>
                                {subWs.name}
                                {subWs.isDefault && (
                                  <span className="text-[9px] bg-white/20 text-white px-1 rounded">DEFAULT</span>
                                )}
                              </span>
                              <span className="text-xs text-blue-200">{subWs.articleCount}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* No Results Message */}
                {filteredWorkspaces.length === 0 && workspaceSearchQuery && (
                  <div className="md:col-span-3 text-center py-8">
                    <div className="w-16 h-16 rounded-full bg-white/10 flex items-center justify-center mx-auto mb-3">
                      <i className="ri-search-line text-white/40 text-3xl"></i>
                    </div>
                    <p className="text-white font-medium">No workspaces found</p>
                    <p className="text-blue-200 text-sm mt-1">Try a different search term or create a new workspace</p>
                  </div>
                )}
              </div>
            </div>

            {/* Create Workspace Modal */}
            {showCreateWorkspace && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateWorkspace(false)}>
                <div
                  className="bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-600 rounded-xl p-6 w-full max-w-lg mx-4 shadow-2xl"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex items-center justify-between mb-6">
                    <h4 className="text-white text-lg font-semibold flex items-center gap-2">
                      <i className="ri-add-circle-line"></i>
                      Create New Workspace
                    </h4>
                    <button
                      onClick={() => {
                        setShowCreateWorkspace(false);
                        setNewWorkspace({ name: '', description: '', icon: 'ri-folder-line', color: 'blue' });
                      }}
                      className="text-white/60 hover:text-white transition-colors"
                    >
                      <i className="ri-close-line text-2xl"></i>
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-blue-100 text-sm mb-1">Workspace Name *</label>
                      <input
                        type="text"
                        value={newWorkspace.name}
                        onChange={(e) => setNewWorkspace({ ...newWorkspace, name: e.target.value })}
                        placeholder="e.g., Marketing Team"
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/50 focus:bg-white/15"
                        autoFocus
                      />
                    </div>

                    <div>
                      <label className="block text-blue-100 text-sm mb-1">Description</label>
                      <input
                        type="text"
                        value={newWorkspace.description}
                        onChange={(e) => setNewWorkspace({ ...newWorkspace, description: e.target.value })}
                        placeholder="Brief description of this workspace"
                        className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/40 text-sm focus:outline-none focus:border-white/50 focus:bg-white/15"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-blue-100 text-sm mb-2">Icon</label>
                        <div className="flex flex-wrap gap-2">
                          {workspaceIcons.map(item => (
                            <button
                              key={item.icon}
                              onClick={() => setNewWorkspace({ ...newWorkspace, icon: item.icon })}
                              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${
                                newWorkspace.icon === item.icon
                                  ? 'bg-white text-blue-600 shadow-lg'
                                  : 'bg-white/10 text-white hover:bg-white/20'
                              }`}
                              title={item.label}
                            >
                              <i className={`${item.icon} text-lg`}></i>
                            </button>
                          ))}
                        </div>
                      </div>

                      <div>
                        <label className="block text-blue-100 text-sm mb-2">Color</label>
                        <div className="flex flex-wrap gap-2">
                          {workspaceColors.map(item => (
                            <button
                              key={item.color}
                              onClick={() => setNewWorkspace({ ...newWorkspace, color: item.color })}
                              className={`w-9 h-9 rounded-lg ${item.bg} transition-all ${
                                newWorkspace.color === item.color
                                  ? 'ring-2 ring-white ring-offset-2 ring-offset-transparent scale-110'
                                  : 'opacity-60 hover:opacity-100'
                              }`}
                              title={item.label}
                            />
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="bg-white/10 rounded-lg p-4 mt-4">
                      <p className="text-blue-200 text-xs mb-2">Preview</p>
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-lg ${
                          workspaceColors.find(c => c.color === newWorkspace.color)?.bg || 'bg-blue-500'
                        } flex items-center justify-center`}>
                          <i className={`${newWorkspace.icon} text-white text-2xl`}></i>
                        </div>
                        <div>
                          <p className="text-white font-medium">
                            {newWorkspace.name || 'Workspace Name'}
                          </p>
                          <p className="text-blue-200 text-sm">
                            {newWorkspace.description || 'No description'}
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                      <button
                        onClick={() => {
                          setShowCreateWorkspace(false);
                          setNewWorkspace({ name: '', description: '', icon: 'ri-folder-line', color: 'blue' });
                        }}
                        className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-white/10 hover:bg-white/20 transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleCreateWorkspace}
                        disabled={!newWorkspace.name.trim()}
                        className={`px-6 py-2 rounded-lg text-sm font-medium transition-all ${
                          newWorkspace.name.trim()
                            ? 'bg-white text-blue-600 hover:bg-blue-50 shadow-lg'
                            : 'bg-white/20 text-white/40 cursor-not-allowed'
                        }`}
                      >
                        <i className="ri-check-line mr-1"></i>
                        Create Workspace
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Collapsible Filter Panel */}
        <div className={`transition-all duration-300 ease-in-out ${showFilters ? 'max-h-[200px] opacity-100 mt-4' : 'max-h-0 opacity-0 mt-0 overflow-hidden'}`}>
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-white/20 flex items-center justify-center">
                  <i className="ri-filter-3-line text-white"></i>
                </div>
                <div>
                  <h3 className="text-white font-semibold">Filters</h3>
                  <p className="text-blue-100 text-xs">Filter articles by workspace, department & location</p>
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="text-sm text-white/80 hover:text-white flex items-center gap-1 px-3 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition-all"
                >
                  <i className="ri-close-circle-line"></i>
                  Clear All
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              {/* Workspace Dropdown */}
              <div>
                <label className="block text-blue-100 text-xs mb-1.5 font-medium">Workspace</label>
                <select
                  ref={workspaceSelectRef}
                  value={filterWorkspace}
                  onChange={(e) => handleFilterChange('workspace', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/50 focus:ring-2 focus:ring-white/30 appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}
                >
                  <option value="all" className="bg-gray-800">All Workspaces</option>
                  {workspaces.map(ws => (
                    <option key={ws.id} value={ws.id} className="bg-gray-800">{ws.name}</option>
                  ))}
                </select>
              </div>

              {/* Department Dropdown */}
              <div>
                <label className="block text-blue-100 text-xs mb-1.5 font-medium">Department</label>
                <select
                  ref={departmentSelectRef}
                  value={filterDepartment}
                  onChange={(e) => handleFilterChange('department', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/50 focus:ring-2 focus:ring-white/30 appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}
                >
                  <option value="all" className="bg-gray-800">All Departments</option>
                  {filteredDepartments.map(dept => (
                    <option key={dept.id} value={dept.id} className="bg-gray-800">{dept.name}</option>
                  ))}
                </select>
              </div>

              {/* Location Dropdown */}
              <div>
                <label className="block text-blue-100 text-xs mb-1.5 font-medium">Location</label>
                <select
                  ref={locationSelectRef}
                  value={filterLocation}
                  onChange={(e) => handleFilterChange('location', e.target.value)}
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-sm focus:outline-none focus:border-white/50 focus:ring-2 focus:ring-white/30 appearance-none cursor-pointer"
                  style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '1.5em 1.5em' }}
                >
                  <option value="all" className="bg-gray-800">All Locations</option>
                  {locations.map(loc => (
                    <option key={loc.id} value={loc.id} className="bg-gray-800">{loc.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Active Filters Display */}
            {hasActiveFilters && (
              <div className="mt-3 pt-3 border-t border-white/10 flex items-center flex-wrap gap-2">
                <span className="text-xs text-white/60">Active:</span>
                {filterWorkspace !== 'all' && (
                  <span className="bg-blue-500/30 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <i className="ri-building-4-line mr-1"></i>
                    {workspaces.find(w => w.id === filterWorkspace)?.name}
                    <button onClick={() => handleFilterChange('workspace', 'all')} className="ml-1.5 hover:text-white/80">
                      <i className="ri-close-line"></i>
                    </button>
                  </span>
                )}
                {filterDepartment !== 'all' && (
                  <span className="bg-purple-500/30 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <i className="ri-folder-3-line mr-1"></i>
                    {departments.find(d => d.id === filterDepartment)?.name}
                    <button onClick={() => handleFilterChange('department', 'all')} className="ml-1.5 hover:text-white/80">
                      <i className="ri-close-line"></i>
                    </button>
                  </span>
                )}
                {filterLocation !== 'all' && (
                  <span className="bg-green-500/30 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center">
                    <i className="ri-map-pin-line mr-1"></i>
                    {locations.find(l => l.id === filterLocation)?.name}
                    <button onClick={() => handleFilterChange('location', 'all')} className="ml-1.5 hover:text-white/80">
                      <i className="ri-close-line"></i>
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Circular/GO Modal */}
        {showCircularGOModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCircularGOModal(false)}>
            <div
              className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className={`px-6 py-4 border-b ${circularGOType === 'circular' ? 'bg-green-50' : 'bg-purple-50'}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg ${circularGOType === 'circular' ? 'bg-green-100' : 'bg-purple-100'} flex items-center justify-center`}>
                      <i className={`${circularGOType === 'circular' ? 'ri-file-text-line text-green-600' : 'ri-government-line text-purple-600'} text-xl`}></i>
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {circularGOType === 'circular' ? 'New Circular' : 'New Government Order (GO)'}
                      </h3>
                      <p className="text-sm text-gray-500">
                        {circularGOType === 'circular' ? 'Add a new circular document' : 'Add a new government order'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowCircularGOModal(false)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <i className="ri-close-line text-2xl"></i>
                  </button>
                </div>
              </div>

              {/* Modal Body */}
              <div className="p-6 space-y-5">
                {/* Number Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    {circularGOType === 'circular' ? 'Circular Number' : 'GO Number'} <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={circularGOForm.number}
                    onChange={(e) => setCircularGOForm({ ...circularGOForm, number: e.target.value })}
                    placeholder={circularGOType === 'circular' ? 'e.g., CIR/2024/001' : 'e.g., GO/2024/001'}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Description Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={circularGOForm.description}
                    onChange={(e) => setCircularGOForm({ ...circularGOForm, description: e.target.value })}
                    placeholder="Brief description of the document..."
                    rows={3}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  />
                </div>

                {/* Attachment Field */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Attachment <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors">
                    {circularGOForm.attachment ? (
                      <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                        <div className="flex items-center gap-3">
                          <i className="ri-file-pdf-line text-red-500 text-2xl"></i>
                          <div className="text-left">
                            <p className="text-sm font-medium text-gray-700">{circularGOForm.attachment.name}</p>
                            <p className="text-xs text-gray-500">
                              {(circularGOForm.attachment.size / 1024).toFixed(1)} KB
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => setCircularGOForm({ ...circularGOForm, attachment: null })}
                          className="text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    ) : (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf,.doc,.docx,.xls,.xlsx"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              setCircularGOForm({ ...circularGOForm, attachment: file });
                            }
                          }}
                          className="hidden"
                        />
                        <div className="py-4">
                          <i className="ri-upload-cloud-2-line text-4xl text-gray-400"></i>
                          <p className="mt-2 text-sm text-gray-600">Click to upload or drag and drop</p>
                          <p className="text-xs text-gray-400 mt-1">PDF, DOC, DOCX, XLS, XLSX (max 10MB)</p>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Departments Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Departments
                  </label>
                  <div className="border border-gray-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                    <div className="flex flex-wrap gap-2">
                      {departments.map(dept => {
                        const isSelected = circularGOForm.departments.includes(dept.id);
                        return (
                          <button
                            key={dept.id}
                            type="button"
                            onClick={() => {
                              if (isSelected) {
                                setCircularGOForm({
                                  ...circularGOForm,
                                  departments: circularGOForm.departments.filter(d => d !== dept.id)
                                });
                              } else {
                                setCircularGOForm({
                                  ...circularGOForm,
                                  departments: [...circularGOForm.departments, dept.id]
                                });
                              }
                            }}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              isSelected
                                ? 'bg-blue-100 text-blue-700 border border-blue-300'
                                : 'bg-gray-100 text-gray-600 border border-gray-200 hover:bg-gray-200'
                            }`}
                          >
                            {isSelected && <i className="ri-check-line mr-1"></i>}
                            {dept.name}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  {circularGOForm.departments.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{circularGOForm.departments.length} department(s) selected</p>
                  )}
                </div>

                {/* Locations Multi-Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Locations
                  </label>
                  <div className="border border-gray-300 rounded-lg overflow-hidden">
                    {/* Search Input */}
                    <div className="p-2 border-b border-gray-200 bg-gray-50">
                      <div className="relative">
                        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type="text"
                          value={locationSearch}
                          onChange={(e) => setLocationSearch(e.target.value)}
                          placeholder="Search locations..."
                          className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        {locationSearch && (
                          <button
                            onClick={() => setLocationSearch('')}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                          >
                            <i className="ri-close-line"></i>
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Selected locations shown at top */}
                    {circularGOForm.locations.length > 0 && (
                      <div className="p-2 border-b border-gray-200 bg-green-50">
                        <div className="flex flex-wrap gap-1.5">
                          {circularGOForm.locations.map(locId => {
                            const loc = locations.find(l => l.id === locId);
                            return loc ? (
                              <span
                                key={locId}
                                className="inline-flex items-center px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"
                              >
                                {loc.name}
                                <button
                                  onClick={() => setCircularGOForm({
                                    ...circularGOForm,
                                    locations: circularGOForm.locations.filter(l => l !== locId)
                                  })}
                                  className="ml-1 hover:text-green-900"
                                >
                                  <i className="ri-close-line"></i>
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                    {/* Location options */}
                    <div className="p-2 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-2">
                        {locations
                          .filter(loc => !locationSearch || loc.name.toLowerCase().includes(locationSearch.toLowerCase()))
                          .filter(loc => !circularGOForm.locations.includes(loc.id))
                          .map(loc => (
                            <button
                              key={loc.id}
                              type="button"
                              onClick={() => setCircularGOForm({
                                ...circularGOForm,
                                locations: [...circularGOForm.locations, loc.id]
                              })}
                              className="px-3 py-1.5 rounded-full text-xs font-medium transition-all bg-gray-100 text-gray-600 border border-gray-200 hover:bg-green-100 hover:text-green-700 hover:border-green-300"
                            >
                              <i className="ri-map-pin-line mr-1"></i>
                              {loc.name}
                            </button>
                          ))}
                        {locations.filter(loc => !locationSearch || loc.name.toLowerCase().includes(locationSearch.toLowerCase())).filter(loc => !circularGOForm.locations.includes(loc.id)).length === 0 && (
                          <p className="text-xs text-gray-500 italic py-2">
                            {locationSearch ? 'No matching locations found' : 'All locations selected'}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  {circularGOForm.locations.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{circularGOForm.locations.length} location(s) selected</p>
                  )}
                </div>

                {/* Tags Select */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Tags
                  </label>
                  {(() => {
                    // Use tags from API
                    const allTags = apiTags.map(t => t.ctgry_nm);
                    const filteredTags = allTags.filter(tag => !tagSearch || tag.toLowerCase().includes(tagSearch.toLowerCase()));
                    return (
                      <div className="border border-gray-300 rounded-lg overflow-hidden">
                        {/* Search Input */}
                        <div className="p-2 border-b border-gray-200 bg-gray-50">
                          <div className="relative">
                            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                            <input
                              type="text"
                              value={tagSearch}
                              onChange={(e) => setTagSearch(e.target.value)}
                              placeholder="Search tags..."
                              className="w-full pl-9 pr-8 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                            />
                            {tagSearch && (
                              <button
                                onClick={() => setTagSearch('')}
                                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                              >
                                <i className="ri-close-line"></i>
                              </button>
                            )}
                          </div>
                        </div>
                        {/* Selected tags shown at top */}
                        {circularGOForm.tags.length > 0 && (
                          <div className="p-2 border-b border-gray-200 bg-purple-50">
                            <div className="flex flex-wrap gap-1.5">
                              {circularGOForm.tags.map(tag => (
                                <span
                                  key={tag}
                                  className="inline-flex items-center px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium"
                                >
                                  {tag}
                                  <button
                                    onClick={() => setCircularGOForm({
                                      ...circularGOForm,
                                      tags: circularGOForm.tags.filter(t => t !== tag)
                                    })}
                                    className="ml-1 hover:text-purple-900"
                                  >
                                    <i className="ri-close-line"></i>
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {/* Tag options */}
                        <div className="p-2 max-h-32 overflow-y-auto">
                          <div className="flex flex-wrap gap-2">
                            {filteredTags
                              .filter(tag => !circularGOForm.tags.includes(tag))
                              .map(tag => (
                                <button
                                  key={tag}
                                  type="button"
                                  onClick={() => setCircularGOForm({
                                    ...circularGOForm,
                                    tags: [...circularGOForm.tags, tag]
                                  })}
                                  className="px-3 py-1.5 rounded-full text-xs font-medium transition-all bg-gray-100 text-gray-600 border border-gray-200 hover:bg-purple-100 hover:text-purple-700 hover:border-purple-300"
                                >
                                  <i className="ri-price-tag-3-line mr-1"></i>
                                  {tag}
                                </button>
                              ))}
                            {filteredTags.filter(tag => !circularGOForm.tags.includes(tag)).length === 0 && (
                              <p className="text-xs text-gray-500 italic py-2">
                                {tagSearch ? 'No matching tags found' : 'All tags selected'}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                  {circularGOForm.tags.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">{circularGOForm.tags.length} tag(s) selected</p>
                  )}
                </div>
              </div>

              {/* Modal Footer */}
              <div className="px-6 py-4 border-t bg-gray-50 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowCircularGOModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    console.log('Creating:', circularGOType, circularGOForm);
                    // TODO: API call to create circular/GO
                    setShowCircularGOModal(false);
                    setCircularGOForm({ number: '', description: '', attachment: null, departments: [], locations: [], tags: [] });
                  }}
                  disabled={!circularGOForm.number || !circularGOForm.description || !circularGOForm.attachment}
                  className={`px-6 py-2 rounded-lg font-medium text-sm transition-all ${
                    circularGOForm.number && circularGOForm.description && circularGOForm.attachment
                      ? circularGOType === 'circular'
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                      : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}
                >
                  <i className={`${circularGOType === 'circular' ? 'ri-file-text-line' : 'ri-government-line'} mr-2`}></i>
                  Create {circularGOType === 'circular' ? 'Circular' : 'Government Order'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
